// The editor's right-hand rail: what is set so far, one line per step, each
// line a way back to the step that sets it — and at the bottom whether the
// campaign can go, or how many things are in the way.

import type { ReactNode } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { count } from "@/components/emc/format";
import { labelOf } from "@/components/emc/status";
import { cn } from "@/lib/utils";
import type { Editor } from "@/pages/campaigns/editor/context";
import { draftStarts, startsLine } from "@/pages/campaigns/model";
import type { StepKey } from "@/pages/campaigns/model";
import { CountryCodes } from "@/pages/campaigns/parts";
import { sendingCountries } from "@/pages/campaigns/checks";

function Row({
  label,
  children,
  step,
  onGo,
}: {
  label: string;
  children: ReactNode;
  step: StepKey;
  onGo: (step: StepKey) => void;
}) {
  return (
    <div className="flex flex-col gap-0.5 border-b border-line py-2.5 last:border-b-0">
      <div className="flex items-baseline justify-between gap-2">
        <span className="caps-label">{label}</span>
        <button
          type="button"
          className="cursor-pointer text-label text-blue-ink hover:underline"
          onClick={() => onGo(step)}
        >
          módosítás
        </button>
      </div>
      <div className="min-w-0 break-words">{children}</div>
    </div>
  );
}

export function SummaryPanel({ editor }: { editor: Editor }) {
  const { summary, estimate, timing, checks, go, identityOf } = editor;
  const countries = sendingCountries(summary);
  const zoneOf = (country: string) => identityOf(country)?.timezone ?? "UTC";
  const starts =
    timing.mode === "later" && timing.date ? startsLine(draftStarts(timing, countries, zoneOf), zoneOf) : null;
  const total = (estimate.rows ?? [])
    .filter((row) => countries.includes(row.country))
    .reduce((sum, row) => sum + row.recipients, 0);
  const failing = checks.filter((check) => check.blocking && check.status === "fail");
  const pending = checks.filter((check) => check.blocking && check.status === "pending");
  const approved = summary.countries.length - editor.detail.unapproved.length;
  // Localised A/B: every (country, arm) cell is approved on its own.
  const cellsTotal = countries.length * summary.ab_arms.length;
  const cellsOpen = summary.unapproved_arms.filter((cell) =>
    countries.includes(cell.split("/")[0] ?? ""),
  ).length;

  return (
    <Card className="gap-2">
      <CardHeader>
        <CardTitle>Összefoglaló</CardTitle>
      </CardHeader>
      <CardContent>
        <Row label="Név" step="alapok" onGo={go}>
          <span className="font-medium">{summary.name}</span>
          <span className="block text-label text-muted-foreground">
            {labelOf("campaignMode", summary.mode)}
            {summary.mode === "localized" && summary.source_country ? `, forrás: ${summary.source_country}` : ""}
          </span>
        </Row>
        <Row label="Országok" step="cimzettek" onGo={go}>
          <CountryCodes countries={summary.countries} />
        </Row>
        <Row label="Címzettek" step="cimzettek" onGo={go}>
          {estimate.rows === null ? (
            <span className="text-muted-foreground">{estimate.loading ? "számolás…" : "még nincs becslés"}</span>
          ) : (
            <>
              <span className="font-semibold">{count(total)}</span>
              <span className="block text-label text-muted-foreground">
                becslés, a kizárások után
                {estimate.at !== null && `, ${estimate.at.toLocaleTimeString("hu-HU", { hour: "2-digit", minute: "2-digit" })}`}
              </span>
            </>
          )}
        </Row>
        <Row label="Levél" step="tartalom" onGo={go}>
          {summary.letter === null ? (
            <span className="text-muted-foreground">nincs kiválasztva</span>
          ) : (
            <>
              {summary.letter.template_name}{" "}
              <span className="data-mono text-muted-foreground">v{summary.letter.version}</span>
            </>
          )}
        </Row>
        <Row label="Tárgy" step="tartalom" onGo={go}>
          {summary.ab !== null ? (
            <span>
              A/B teszt, {summary.ab_arms.length} variáns
              <span className="block text-label text-muted-foreground">
                {summary.ab_arms.map((arm) => `${arm.key}: ${arm.subject || "—"}`).join(" · ")}
              </span>
              {summary.mode === "localized" && (
                <span className="block text-label">
                  {cellsTotal - cellsOpen} / {cellsTotal} variáns jóváhagyva ·{" "}
                  {approved} / {summary.countries.length} ország szövege
                </span>
              )}
            </span>
          ) : summary.mode === "localized" ? (
            <span>
              {approved} / {summary.countries.length} ország jóváhagyva
            </span>
          ) : (
            <span className="text-muted-foreground">a sablonból, nyelvenként</span>
          )}
        </Row>
        <Row label="Időzítés" step="idozites" onGo={go}>
          {timing.mode === "now" ? (
            "Most"
          ) : starts !== null ? (
            <>
              <span className="data-mono">{starts.viewer}</span>
              {starts.local !== null && (
                <span className="block text-label text-muted-foreground">({starts.local})</span>
              )}
            </>
          ) : (
            <span className="text-muted-foreground">nincs megadva</span>
          )}
        </Row>
        <div
          className={cn(
            "mt-3 rounded-control px-3 py-2",
            failing.length > 0 ? "bg-warn-soft text-warn" : pending.length > 0 ? "bg-surface-2 text-muted-foreground" : "bg-green-soft text-green-ink",
          )}
        >
          <button type="button" className="w-full cursor-pointer text-left font-medium" onClick={() => go("attekintes")}>
            {failing.length > 0
              ? `${failing.length} tennivaló az indítás előtt`
              : pending.length > 0
                ? "Ellenőrzés folyamatban…"
                : "Indítható — Áttekintés és küldés"}
          </button>
        </div>
      </CardContent>
    </Card>
  );
}
